/* PM v4: PDP gallery autoplay. Rebuilt 11 Oct 2026 (owner note 13), all products, phone and desktop.
   Perf rewrite 14 Oct 2026: no polling timer. One setTimeout is armed only while the gallery is on screen and the tab is
   visible; IntersectionObserver and visibilitychange arm/clear it, interaction events re-arm it.
   Perf rewrite 2, 15 Oct 2026 (Lighthouse NO_TTI_CPU_IDLE_PERIOD): the first slide change used to land about 4s after
   DOMContentLoaded, inside the window Lighthouse needs to be quiet. Now:
   - Nothing is armed until window load AND an idle callback (requestIdleCallback, 4s fallback) have both happened.
   - The first slide change is 12s after that point, then every 4s.
   - The change itself is an opacity fade (compositor only) around an instant jump, not a smooth scroll that fires
     scroll events and style work for 300-500 ms. Reduced motion: instant jump, no fade.
   - No layout reads in the timer path except one slide-step measure per change (no per-slide loop).
   Behaviour otherwise unchanged:
   - Always opens on photo 1, the hero: reset to the first slide with an instant jump on load and on bfcache restores.
   - Pauses on hover, keyboard focus, touch or swipe, thumbnail click and while the zoom modal is open.
     It resumes 4s after the interaction ends (6s after a thumbnail click or a swipe), from the photo
     the shopper left it on.
   - Waits while the tab is hidden or the gallery is off screen (no timer at all while waiting).
   History: 8 Oct first version (3.5s, setInterval). 10 Oct switched off because pages opened mid-slide on an
   infographic. Root causes fixed 11 Oct: the old timer kept running from page load whatever the gallery state was,
   the slide moved with a smooth scroll that a slow page could show half way, and nothing put the gallery
   back on photo 1 when the browser restored a scrolled gallery. */
(function () {
  'use strict';
  var DELAY = 4000;
  var FIRST_DELAY = 12000;
  var RESUME_AFTER_TOUCH = 6000;
  var RESUME_AFTER_THUMB = 6000;
  var FADE_MS = 160;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ready = window load + idle. Galleries wire their listeners at once but only arm a timer after this. */
  var ready = false, waiting = [];
  function markReady() {
    if (ready) return;
    ready = true;
    var w = waiting; waiting = [];
    for (var i = 0; i < w.length; i++) { try { w[i](); } catch (e) {} }
  }
  function whenReady(cb) { if (ready) cb(); else waiting.push(cb); }
  function afterLoad() {
    var done = false;
    function fire() { if (done) return; done = true; markReady(); }
    setTimeout(fire, 4000);
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fire, { timeout: 4000 });
    else setTimeout(fire, 200);
  }
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });

  function modalOpen() {
    return !!document.querySelector('product-modal[open], .product-media-modal[open], .product-popup-modal[open]');
  }

  function setup(gallery) {
    if (gallery.dataset.pmAutoplay === 'on') return;
    var viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;
    var list = viewer.querySelector('ul');
    if (!list) return;
    var slides = Array.prototype.filter.call(list.children, function (el) { return el.dataset && el.dataset.mediaId; });
    if (slides.length < 2) return;
    gallery.dataset.pmAutoplay = 'on';

    var hover = false, focus = false, holdUntil = 0, touching = false, onScreen = true, started = false;
    var timer = null, fade = null;

    function scrollable() { return list.scrollWidth > list.clientWidth + 5; }

    /* One measure of the slide step instead of reading every slide's offset. */
    function currentIndex() {
      if (scrollable()) {
        var base = slides[0].offsetLeft;
        var step = slides[1].offsetLeft - base;
        if (step <= 0) return 0;
        var i = Math.round((list.scrollLeft - base) / step);
        return i < 0 ? 0 : (i > slides.length - 1 ? slides.length - 1 : i);
      }
      for (var k = 0; k < slides.length; k++) {
        if (slides[k].classList.contains('is-active')) return k;
      }
      return 0;
    }

    function setActiveClass(i) {
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
    }

    function syncThumb(i) {
      var thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!thumbs) return;
      var t = thumbs.querySelector('[data-target="' + slides[i].dataset.mediaId + '"] button');
      if (!t) return;
      thumbs.querySelectorAll('button[aria-current]').forEach(function (b) { b.removeAttribute('aria-current'); });
      t.setAttribute('aria-current', 'true');
    }

    /* The theme sets scroll-behavior: smooth on the list, which makes scrollTo({behavior:'auto'}) animate and fire a
       scroll event (and the theme's slider update(), about 100 ms each) every frame. Force a single instant jump. */
    function snap(left) {
      var prev = list.style.scrollBehavior;
      list.style.scrollBehavior = 'auto';
      list.scrollLeft = left;
      list.style.scrollBehavior = prev;
    }

    function jump(i) {
      setActiveClass(i);
      snap(slides[i].offsetLeft);
      syncThumb(i);
    }

    function cancelFade() {
      if (fade) { try { fade.cancel(); } catch (e) {} fade = null; }
    }

    /* Opacity-only transition: fade out, instant jump while invisible, fade in. */
    function go(i) {
      if (scrollable()) {
        if (reduce || typeof list.animate !== 'function') { jump(i); return; }
        cancelFade();
        var out = list.animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, easing: 'ease-out', fill: 'forwards' });
        fade = out;
        out.onfinish = function () {
          if (fade !== out) return;
          if (!holding()) {
            jump(i);
            list.animate([{ opacity: 0 }, { opacity: 1 }], { duration: FADE_MS, easing: 'ease-in' });
          }
          cancelFade();
        };
      } else if (typeof gallery.setActiveMedia === 'function' && gallery.elements && gallery.elements.thumbnails) {
        gallery.setActiveMedia(slides[i].dataset.mediaId, false);
      } else {
        setActiveClass(i);
        syncThumb(i);
      }
    }

    /* The user is interacting (hover, focus, finger down): no timer runs; the release handler re-arms it. */
    function holding() { return hover || focus || touching; }

    function clearTimer() {
      if (timer) { clearTimeout(timer); timer = null; }
    }

    /* Arm the single timer. It never runs before load+idle, while the gallery is off screen or the tab is hidden. */
    function arm(ms) {
      clearTimer();
      if (!started || !onScreen || document.hidden || holding()) return;
      timer = setTimeout(tick, ms);
    }

    function tick() {
      timer = null;
      if (!onScreen || document.hidden || holding()) return;
      var now = Date.now();
      if (now < holdUntil) { arm(holdUntil - now); return; }
      if (modalOpen()) { arm(DELAY); return; }
      go((currentIndex() + 1) % slides.length);
      arm(DELAY);
    }

    /* Always start on photo 1, the hero, with no animation. Returns true when it had to move the gallery. */
    function toFirst() {
      if (list.scrollLeft !== 0 || currentIndex() !== 0) {
        cancelFade();
        snap(0);
        setActiveClass(0);
        syncThumb(0);
        return true;
      }
      return false;
    }

    var finePointer = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (finePointer) {
      gallery.addEventListener('mouseenter', function () { hover = true; clearTimer(); });
      gallery.addEventListener('mouseleave', function () { hover = false; arm(DELAY); });
    }
    gallery.addEventListener('focusin', function (e) {
      if (e.target && e.target.matches && e.target.matches(':focus-visible')) { focus = true; clearTimer(); }
    });
    gallery.addEventListener('focusout', function (e) {
      if (!gallery.contains(e.relatedTarget)) { focus = false; arm(DELAY); }
    });
    gallery.addEventListener('touchstart', function () { touching = true; clearTimer(); cancelFade(); list.style.opacity = ''; }, { passive: true });
    function touchEnd() {
      touching = false;
      holdUntil = Date.now() + RESUME_AFTER_TOUCH;
      arm(RESUME_AFTER_TOUCH);
    }
    gallery.addEventListener('touchend', touchEnd, { passive: true });
    gallery.addEventListener('touchcancel', touchEnd, { passive: true });
    gallery.addEventListener('click', function (e) {
      if (e.target.closest('[id^="GalleryThumbnails"], .slider-button, .product__media-toggle, modal-opener')) {
        holdUntil = Date.now() + RESUME_AFTER_THUMB;
        arm(RESUME_AFTER_THUMB);
      }
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var on = entries[entries.length - 1].isIntersecting;
        if (on === onScreen) return;
        onScreen = on;
        if (on) arm(DELAY); else clearTimer();
      }, { threshold: 0.4 }).observe(viewer);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearTimer(); else arm(DELAY);
    });

    window.addEventListener('pageshow', function (e) { if (e.persisted) { toFirst(); arm(DELAY); } });

    /* One cheap early check so a browser-restored scroll position never shows a mid-slide gallery. */
    if (list.scrollLeft !== 0) toFirst();

    whenReady(function () {
      started = true;
      if (!touching && !hover && Date.now() >= holdUntil) toFirst();
      arm(FIRST_DELAY);
    });
  }

  function init() {
    var galleries = document.querySelectorAll('[id^="MainProduct-"] media-gallery, section[id*="__main"] media-gallery, .product media-gallery');
    Array.prototype.forEach.call(galleries, function (g) {
      if (customElements.get('media-gallery')) { setup(g); }
      else { customElements.whenDefined('media-gallery').then(function () { setup(g); }); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
